import { ApiResponse } from '../api/types/ApiResponse';
import { DataAttributes } from './DataAttributes';
import { GameVm } from './GameVm';
import { BettingSideVm } from './BettingSideVm';
import { SportsbookVm } from './SportsbookVm';
import { BettingLineData } from '../api/types/BettingLineData';
import { BettingSideData } from '../api/types/BettingSideData';
import { TeamVm } from './TeamVm';
import { BettingSideEnum } from './enums/BettingSideEnum';
import { currencyFormatter } from '../helper/currencyFormatter';
import { deepLinkUrl } from '../constant/constant';

export class BetVm {
    constructor(apiData: ApiResponse, dataAttributes: DataAttributes) {
        this.game = new GameVm(apiData.game);

        // Get the BettingLineData based on the sportsbook data attribute
        const selectedBook = apiData.odds[dataAttributes.selectedSportsbook];
        const sportsbookVm = new SportsbookVm(selectedBook);
        // Using the selected BettingLineData, get the BettingSideData based on the selected side data attribute
        const bettingSideData = this.getBettingSideData(selectedBook, dataAttributes.selectedSide);
        // If it's not a Total, we will need the TeamVm for the BettingSideVm
        const teamVm = this.getTeamVm(dataAttributes.selectedSide);
        // If it's a Total, we need to know if we picked over or under
        const overUnder = this.getOverUnder(dataAttributes.selectedSide);

        if (!bettingSideData) {
            throw new Error(`No valid betting data available for the selected side: ${dataAttributes.selectedSide}`);
        }

        this.selectedTeam = this.getTeamVm(dataAttributes.selectedSide, true);

        // Create the selected bet BettingSideVm
        this.selectedBet = new BettingSideVm(bettingSideData, sportsbookVm, overUnder);

        // Create the List of bets of the same market selected for odds comparison (this includes the selected bet as well)
        this.comparisonBets = Object.keys(apiData.odds)
            .map(sportsbook => {
                const sideData = this.getBettingSideData(apiData.odds[sportsbook], dataAttributes.selectedSide);
                if (!sideData) {
                    throw new Error(`No valid betting data available for comparison bets for sportsbook: ${sportsbook}`);
                }
                return new BettingSideVm(sideData, new SportsbookVm(apiData.odds[sportsbook]), overUnder);
            });
        this.straightWager = currencyFormatter.format(10.00);
        this.isActiveLeg = true;
        this.betTitle = ''; // initialize bet title to empty string
        this.betData = ''; // initialize bet odds data to empty string
        this.setBetTitle(teamVm, this.selectedBet); // re-assign bet title conditionally based on odds type
        // Set the bet description based on the oddsScope.. if its competition its Full Game so we dont include it
        if (bettingSideData.oddsScope === "Competition") {
            this.betDescription = `${bettingSideData.oddsType} \u2022 ${this.game.awayTeam.shortName} @ ${this.game.homeTeam.shortName}`;
        } else {
            this.betDescription = `${bettingSideData.oddsScope} ${bettingSideData.oddsType} \u2022 ${this.game.awayTeam.shortName} @ ${this.game.homeTeam.shortName}`;
        }       
        this.deepLink = this.buildDeepLink(sportsbookVm.providerBookId, bettingSideData.marketValue) || this.selectedBet.sportsbook.sportsbookClickout; // if null just send to sign-up page
        this.apiUrl = dataAttributes.apiUrl;
        this.selectedSide = dataAttributes.selectedSide;
        this.selectedSportsbook = dataAttributes.selectedSportsbook;
        this.betKey = `${this.game.gameId}.${bettingSideData.oddsScope}.${this.betTitle}.${this.selectedBet.sportsbook.sportsbookFeedKey}`;
    }

    // Define class properties
    game: GameVm;
    selectedTeam: TeamVm | string;
    selectedBet: BettingSideVm;
    comparisonBets: BettingSideVm[];
    straightWager: string;
    isActiveLeg: boolean; // applicable to parlays where bets have an active/inactive state
    deepLink: string;
    apiUrl: string;
    selectedSide: string;
    selectedSportsbook: string;

    betTitle: string;
    betDescription: string;
    betData: string;
    betKey: string;

    private getBettingSideData(bettingLineData: BettingLineData, selectedSide: string): BettingSideData | null {        
        switch (selectedSide) {
            case BettingSideEnum.Away:
                return bettingLineData.sides.away || null;
            case BettingSideEnum.Home:
                return bettingLineData.sides.home || null;
            case BettingSideEnum.Over:
                return bettingLineData.sides.over || null;
            case BettingSideEnum.Under:
                return bettingLineData.sides.under || null;
            default:
                throw new Error(`Invalid selectedSide value: ${selectedSide}`);
        }
    }

    private getTeamVm(selectedSide: string, stringType = false): TeamVm | string {
        switch (selectedSide) {
            case BettingSideEnum.Home:
                return stringType ? 'homeTeam' : this.game.homeTeam;
            case BettingSideEnum.Away:
                return stringType ? 'awayTeam' : this.game.awayTeam;
            default:
                return '';  // Return '' for 'over' and 'under' or any non-team related bets
        }
    }

    private getOverUnder(selectedSide: string): 'over' | 'under' | null {
        return selectedSide === BettingSideEnum.Over ? 'over' :
               selectedSide === BettingSideEnum.Under ? 'under' : null;
    }

    private buildDeepLink(providerBookId: string, marketValue: string | null): string | null {
        if (!providerBookId || !marketValue) {
            return null;
        }
        return `${deepLinkUrl}${providerBookId}?marketSelection=${marketValue}`;
    }

    private setBetTitle(teamVm: any, sideVm: any) {
        switch (sideVm.oddsType) {
            case 'moneyline':
                if (teamVm) {
                    this.betTitle = `${teamVm.displayName}`;
                    this.betData = this.formatBetData(sideVm.odds, 0);
                }
                break;
            case 'pointspread':
                if (teamVm && sideVm.handicap !== null) {
                    this.betTitle = `${teamVm.displayName}`;
                    this.betData = `${this.formatBetData(sideVm.handicap, 1)} ${this.formatBetData(sideVm.odds, 0)}`;
                }
                break;
            case 'total':
                if (sideVm.overUnder && sideVm.total !== null) {
                    const overUnder = sideVm.overUnder.charAt(0).toUpperCase() + sideVm.overUnder.slice(1);
                    this.betTitle = `${overUnder}`;
                    this.betData = `${sideVm.total} ${this.formatBetData(sideVm.odds, 0)}`;
                }
                break;
            default:
                this.betTitle = '';
        }
    }

    // method which appends a plus for positive handicaps/odds.. dynamic formatting of # of decimal places to include via param
    private formatBetData(value: number, decimalPlaces: number): string {
        return `${value > 0 ? '+' : ''}${value.toFixed(decimalPlaces)}`;
    }
}
